import { ReactNode, useMemo } from "react";
import { ChartConfig } from "@/components/ui/chart";
import { getColor } from "@/components/radixColors";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import {
  stringifyDistinctValues,
  useExploreValueDistribution,
} from "@/v2/entities/explore/Explore.api";
import {
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import {
  isExploreGroupByColumnType,
  isExploreColumnType,
  ExploreColumnType,
  ExploreAggColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  normalizeValue,
  getGroupByColumnConfig,
  getColumnConfig,
  CHART_UNCLASSIFIED_LABEL,
} from "./explore-widgets-utils";

export type ChartItem = {
  value: string;
  values: [string, number][];
};

function getGroupByValue(
  result: Record<string, unknown>,
  groupBy: string
): string | number | boolean | null | undefined {
  const value = result[groupBy];
  if (
    value == undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return undefined;
}

export function useExploreDistributionChart(
  source: ExploreInteractionType = "tickets",
  groupBy: string = "tenantId",
  splitBy: string = "tenantId",
  metric: ExploreAggColumnType = "volume",
  filter?: Filter
): {
  items: ChartItem[];
  isLoading: boolean;
  chartConfig: ChartConfig;
  valueMap: Map<string, string>;
  itemToLabelMap: Map<string, string>;
  groupByLabelFormatter?: (value?: string) => ReactNode;
} {
  const { data, isLoading } = useExploreValueDistribution({
    source,
    groupBy,
    splitBy,
    metric,
    filter,
  });
  const valueMap = useMemo(() => {
    const map = new Map<string, string>();
    const labelsFromValues = new Map<string, string>();

    if (data?.values != null && data.values.length > 0) {
      const values = stringifyDistinctValues(data.values);
      values.forEach((value) => {
        labelsFromValues.set(normalizeValue(value), value);
      });
      if (data.values.some((v) => v == null)) {
        labelsFromValues.set(normalizeValue(null), CHART_UNCLASSIFIED_LABEL);
      }
    }

    if (data?.results != null && data.results.length > 0) {
      const seriesKeys = new Set<string>();
      data.results.forEach((result) => {
        const row = result as unknown as Record<string, unknown>;
        Object.entries(row).forEach(([key, entryValue]) => {
          if (
            key !== groupBy &&
            key !== metric &&
            typeof entryValue === "number"
          ) {
            const normalizedKey =
              key === "null" ? normalizeValue(null) : normalizeValue(key);
            seriesKeys.add(normalizedKey);
          }
        });
        const groupVal = getGroupByValue(row, groupBy);
        const metricVal = row[metric];
        if (
          (groupVal === null || groupVal === undefined) &&
          typeof metricVal === "number"
        ) {
          seriesKeys.add(normalizeValue(null));
        }
      });
      const unknownKey = normalizeValue(null);
      seriesKeys.forEach((key) => {
        map.set(
          key,
          labelsFromValues.get(key) ??
            (key === unknownKey ? CHART_UNCLASSIFIED_LABEL : key)
        );
      });
    } else {
      labelsFromValues.forEach((label, key) => {
        map.set(key, label);
      });
    }

    return map;
  }, [data?.values, data?.results, groupBy, metric]);

  const { items, itemToLabelMap, chartConfig, groupByLabelFormatter } =
    useMemo(() => {
      const chartConfig: ChartConfig = {};
      const items: ChartItem[] = [];
      const itemToLabelMap = new Map<string, string>();
      const isKnownGroupBy = isExploreGroupByColumnType(groupBy);
      const { labelFormatter: groupByLabelFormatter } = isKnownGroupBy
        ? getGroupByColumnConfig(groupBy)
        : { labelFormatter: (value?: string) => value ?? "" };

      const {
        labelFormatter: splitByLabelFormatter,
        colorOverrides: splitByColorOverrides,
      } = isExploreColumnType(splitBy)
        ? getColumnConfig(splitBy)
        : {
            labelFormatter: (value?: string) => value ?? "",
            colorOverrides: undefined as Record<string, string> | undefined,
          };

      data?.results.forEach((result, index) => {
        const row = result as unknown as Record<string, unknown>;
        const value = getGroupByValue(row, groupBy);
        if (value === undefined) {
          return;
        }

        const values: [string, number][] = [];
        Object.entries(row).forEach(([key, entryValue]) => {
          if (key === groupBy || typeof entryValue !== "number") {
            return;
          }

          if (key === "null") {
            values.push([normalizeValue(null), entryValue]);
            return;
          }

          values.push([normalizeValue(key), entryValue]);
        });

        if (values.length === 0) {
          const metricVal = row[metric];
          if (typeof metricVal === "number") {
            values.push([normalizeValue(null), metricVal]);
          } else {
            return;
          }
        }

        const normalizedValue = normalizeValue(value);
        if (
          value != null &&
          (groupBy === "week" || groupBy === "day" || groupBy === "month")
        ) {
          items.push({
            value: value.toString(),
            values,
          });
        } else {
          items.push({
            value: normalizedValue,
            values,
          });
        }

        if (groupBy === "tenantId") {
          const label = exploreInteractionTypeToLabel[source].plural;
          itemToLabelMap.set(normalizedValue, label);
          chartConfig[normalizedValue] = {
            label,
            color: getColor(index++),
          };
          return;
        }

        const displayLabel =
          value == null ? CHART_UNCLASSIFIED_LABEL : value.toString();
        itemToLabelMap.set(normalizedValue, displayLabel);

        chartConfig[normalizedValue] = {
          label:
            value == null
              ? CHART_UNCLASSIFIED_LABEL
              : groupByLabelFormatter(value?.toString()),
        };
      });

      const unknownKey = normalizeValue(null);
      if (splitBy !== "tenantId") {
        [...valueMap].forEach(([key, value], index) => {
          chartConfig[key] = {
            label:
              key === unknownKey
                ? CHART_UNCLASSIFIED_LABEL
                : (splitByLabelFormatter?.(value) ?? value) ||
                  CHART_UNCLASSIFIED_LABEL,
            color: splitByColorOverrides?.[key] ?? getColor(index++),
          };
        });
      } else {
        const label = exploreInteractionTypeToLabel[source].plural;
        const color = getColor();
        [...valueMap].forEach(([key]) => {
          chartConfig[key] = {
            label,
            color,
          };
        });
      }

      if (metric === "avgQaScoreByContent") {
        try {
          for (const item of items) {
            for (let i = 0; i < item.values.length; i++) {
              const findChartConfig = Object.keys(chartConfig).find((key) =>
                key.includes(item.values[i][0])
              );
              if (findChartConfig) {
                item.values[i][0] = findChartConfig;
              }
            }
          }
        } catch (error) {
          console.log("error in useExploreDistributionChart", {
            error,
            items,
            chartConfig,
          });
        }
      }

      return {
        items,
        itemToLabelMap,
        chartConfig,
        groupByLabelFormatter,
      };
    }, [groupBy, source, data?.results, valueMap, splitBy, metric]);

  return {
    items,
    isLoading,
    chartConfig,
    valueMap,
    itemToLabelMap,
    groupByLabelFormatter,
  };
}
